import { useState } from "react";
import { motion } from "framer-motion";
import { soloApi } from "@/lib/api";
import { AGENTS } from "@/lib/agents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Wand2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TONES = ["playful", "punchy", "story-driven", "authoritative"];

export default function Marketer() {
  const a = AGENTS.marketer;
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("playful");
  const [withImage, setWithImage] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await soloApi.marketerDraft(topic, tone, withImage);
      setResult(data);
      toast.success("Draft queued for your approval ✦");
    } catch {
      toast.error("Mica hit a snag. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 pb-16">
      <div className="flex items-center gap-4 mb-8">
        <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity }} className="w-16 h-16 slime-blob overflow-hidden" style={{ background: a.gradient }}>
          <img src={a.avatar} alt="Mica" className="w-full h-full object-cover mix-blend-luminosity" />
        </motion.div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: a.color }}>{a.role}</div>
          <h1 className="font-display text-4xl font-bold" style={{ color: a.color }}>Mica's studio</h1>
          <p className="text-white/70">Give me a topic. I'll draft it.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass rounded-3xl p-6 neon-glow-pink">
          <label className="font-display text-xl font-bold mb-3 block">What's the post about?</label>
          <Input
            data-testid="marketer-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Launching our reusable coffee cup pilot in Berlin next Monday"
            className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12 mb-4"
          />

          <label className="font-mono text-xs uppercase tracking-widest text-white/60 mb-2 block">Tone</label>
          <div className="flex flex-wrap gap-2 mb-4">
            {TONES.map((t) => (
              <button
                key={t}
                data-testid={`marketer-tone-${t}`}
                onClick={() => setTone(t)}
                className={"px-4 py-1.5 rounded-full text-sm font-bold transition-colors " + (tone === t ? "bg-neon-pink text-white neon-glow-pink" : "bg-white/5 text-white/70 hover:bg-white/10")}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6 p-3 rounded-xl bg-white/5 border border-white/10">
            <div>
              <div className="font-semibold">Generate visual too</div>
              <div className="text-xs text-white/60">Uses Nano Banana. Takes ~15s.</div>
            </div>
            <Switch data-testid="marketer-with-image" checked={withImage} onCheckedChange={setWithImage} />
          </div>

          <Button
            data-testid="marketer-generate"
            onClick={generate}
            disabled={!topic.trim() || loading}
            className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 text-white font-bold py-6 neon-glow-pink"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            {loading ? "Mica is drafting…" : "Draft it"}
          </Button>
        </div>

        <div className="glass rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="text-neon-lime" />
            <div className="font-display text-xl font-bold">Preview</div>
          </div>
          {loading && (
            <div className="text-white/60 flex items-center gap-2">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-neon-pink rounded-full animate-pulse" />
                <span className="w-2 h-2 bg-neon-pink rounded-full animate-pulse" style={{ animationDelay: "0.15s" }} />
                <span className="w-2 h-2 bg-neon-pink rounded-full animate-pulse" style={{ animationDelay: "0.3s" }} />
              </div>
              Mica is cooking…
            </div>
          )}
          {!loading && !result && <p className="text-white/60">Your draft will appear here, then head to the Approval Inbox.</p>}
          {result && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="marketer-result" className="space-y-4">
              {result.image_url && <img src={result.image_url} alt="generated" className="w-full rounded-2xl border border-white/10" />}
              <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-4 whitespace-pre-wrap font-mono text-sm text-neon-lime/90">
                {result.post_text}
              </div>
              <p className="text-xs text-white/60">✓ Queued in Approval Inbox. You approve, we (pretend to) publish.</p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
